import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { useConfigPage, BackLink } from './_shared';

/**
 * Logging-Config.
 *
 * Two independent channels + a toggle set:
 *   - `logChannelId` — message-delete / message-edit events. Consumers:
 *      LogsMessageDelete.js + LogsMessageEdit.js (legacy scalar column).
 *   - `auditLogChannelId` — Discord server-events (join/leave/ban/role/channel
 *      changes / voice moves / automod triggers). Consumer: AuditLog.js.
 *
 * `auditEvents` is a per-category on/off map. Default: everything on except
 * `selfRoles` (roles members take themselves — bot lib/auditLog.js keeps it
 * off while unset). Turning a category off drops those event kinds silently
 * (nothing is posted).
 */

export interface AuditToggles {
  members: boolean;
  bans: boolean;
  roles: boolean;
  selfRoles: boolean;
  channels: boolean;
  voice: boolean;
  emojis: boolean;
  automod: boolean;
}
const DEFAULT_TOGGLES: AuditToggles = {
  members: true,
  bans: true,
  roles: true,
  selfRoles: false,
  channels: true,
  voice: true,
  emojis: true,
  automod: true,
};
const TOGGLE_LABELS: Record<keyof AuditToggles, string> = {
  members: 'Member-Events (Join / Leave / Nickname / Timeout)',
  bans: 'Bans & Unbans',
  roles: 'Rollen-Änderungen (erstellt / gelöscht / vergeben / entfernt)',
  selfRoles: 'Self-Role-Änderungen (Self-Roles-Panels, Farben, Regeln akzeptieren, Startrolle)',
  channels: 'Channel-Änderungen (erstellt / gelöscht / umbenannt / Rechte)',
  voice: 'Voice-Events (verschoben / rausgeworfen)',
  emojis: 'Emojis & Sticker (hinzugefügt / gelöscht / geändert)',
  automod: 'AutoMod-Auslöser',
};

export function LoggingConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('logging');
  const [channelId, setChannelId] = useState<string | null>(null);
  const [auditChannelId, setAuditChannelId] = useState<string | null>(null);
  const [toggles, setToggles] = useState<AuditToggles>(DEFAULT_TOGGLES);

  useEffect(() => {
    if (settingsQuery.data) {
      setChannelId((currentConfig.channelId as string) ?? null);
      setAuditChannelId((currentConfig.auditLogChannelId as string) ?? null);
      const stored = (currentConfig.auditEvents as Partial<AuditToggles>) ?? {};
      setToggles({ ...DEFAULT_TOGGLES, ...stored });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <BackLink guildId={guildId} />
      <h1 className="text-2xl font-semibold">Logging — Konfiguration</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Message-Log-Channel</CardTitle>
          <CardDescription>
            Wo gelöschte + editierte Nachrichten geloggt werden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Log-Channel</Label>
            <ChannelPicker
              guildId={guildId}
              value={channelId}
              onChange={setChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Audit-Log-Channel</CardTitle>
          <CardDescription>
            Wo Server-Events geloggt werden — Joins, Bans, Rollen-Änderungen,
            Channel-Änderungen, Voice-Aktionen, Emojis, AutoMod. Separat vom
            Message-Log um beide lesbar zu halten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Audit-Channel</Label>
            <ChannelPicker
              guildId={guildId}
              value={auditChannelId}
              onChange={setAuditChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>

          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Kategorien
            </div>
            {(Object.keys(TOGGLE_LABELS) as Array<keyof AuditToggles>).map((k) => (
              <Switch
                key={k}
                checked={toggles[k]}
                onChange={(e) => setToggles({ ...toggles, [k]: e.target.checked })}
                label={TOGGLE_LABELS[k]}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {saveMut.isSuccess && <span className="text-sm text-success">Gespeichert ✓</span>}
        {saveMut.error && (
          <span className="text-sm text-destructive">
            Fehler: {(saveMut.error as Error).message}
          </span>
        )}
        <Button
          disabled={saveMut.isPending}
          onClick={() =>
            saveMut.mutate({
              moduleConfig: {
                channelId,
                auditLogChannelId: auditChannelId,
                auditEvents: toggles,
              },
              scalars: { logChannelId: channelId },
            })
          }
        >
          {saveMut.isPending ? 'Speichere…' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}
